// src/pages/VolunteerCheckin.js
import React, { useEffect, useState } from "react";
import { ref, get, update, set, child } from "firebase/database";
import { db, auth } from "../firebase";
import { useSearchParams } from "react-router-dom";
import { getPreferredEmailForRoll } from "../utils/identity";
import { getSessionInfo } from "../roles";

export default function VolunteerCheckin() {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);

  // who is the volunteer?
  const [volInfo, setVolInfo] = useState(() => getSessionInfo());
  const roll = (params.get("roll") || "").toUpperCase();
  const slot = params.get("slot") || "";

  // attendance status
  const [already, setAlready] = useState(false);
  const [attAt, setAttAt] = useState(null);
  const [attBy, setAttBy] = useState("");

  // derive volunteer identity robustly
  useEffect(() => {
    // try session first, then auth.currentUser
    const u = auth.currentUser;
    if (!volInfo && u) {
      setVolInfo({
        id: u.uid,
        email: u.email || "",
        name: u.displayName || "",
        role: "volunteer", // UI use; not security
      });
    }
  }, [volInfo]);

  useEffect(() => {
    (async () => {
      if (!roll) { setLoading(false); return; }
      setLoading(true);

      // Load/ensure member
      const mSnap = await get(ref(db, `membersByRoll/${roll}`));
      let m = mSnap.val() || null;

      const preferredEmail = await getPreferredEmailForRoll(roll);
      if (!m) {
        m = { roll, name: roll, branch: "", section: "", teamId: "", email: preferredEmail || "" };
        await set(ref(db, `membersByRoll/${roll}`), m);
      } else if (preferredEmail && m.email !== preferredEmail) {
        await update(ref(db, `membersByRoll/${roll}`), { email: preferredEmail });
        m.email = preferredEmail;
      }

      setCard({
        roll,
        name: m.name || roll,
        branch: m.branch || "",
        section: m.section || "",
        teamId: m.teamId || "",
        email: m.email || preferredEmail || ""
      });

      // Load existing attendance (either tree)
      let attSnap = await get(ref(db, `attendance/${roll}/${slot}`));
      if (!attSnap.exists()) {
        // also try attendanceBySlot mirror
        const bySlotSnap = await get(ref(db, `attendanceBySlot/${slot}/${roll}`));
        if (bySlotSnap.exists()) {
          const v = bySlotSnap.val() || {};
          setAlready(true);
          setAttAt(v.at || v.t || null);
          setAttBy(v.byVol || v.v || "");
        } else {
          setAlready(false);
          setAttAt(null);
          setAttBy("");
        }
      } else {
        const v = attSnap.val() || {};
        setAlready(true);
        setAttAt(v.at || v.t || null);
        setAttBy(v.byVol || v.v || "");
      }

      setLoading(false);
    })();
  }, [roll, slot]);


  async function markPresent() {
  if (!roll || !slot) {
    alert("Invalid QR – missing parameters.");
    return;
  }

  // Ask volunteer for code
  const entered = prompt("Enter the participant's 6-digit code:");
  if (!entered) {
    alert("Check-in cancelled (no code entered).");
    return;
  }

  // Verify with code stored under /codes/{roll}/{slot}/code
  const codeSnap = await get(ref(db, `codes/${roll}/${slot}/code`));
  if (!codeSnap.exists() || String(codeSnap.val()) !== String(entered.trim())) {
    alert("Invalid code! Please verify with participant.");
    return;
  }

  // Prevent duplicate
  const existing = await get(ref(db, `attendance/${roll}/${slot}`));
  if (existing.exists()) {
    alert("Already checked in for this slot.");
    return;
  }

  const now = Date.now();
  const volId   = volInfo?.id || auth.currentUser?.uid || "";
  const volMail = volInfo?.email || auth.currentUser?.email || "";
  const volLbl  = volId || volMail || "unknown";

  const updates = {};
  updates[`attendance/${roll}/${slot}`] = { at: now, byVol: volLbl };
  updates[`attendanceBySlot/${slot}/${roll}`] = { at: now, byVol: volLbl };
  await update(ref(db), updates);

  alert(`Check-in successful for ${roll}`);
  setAlready(true);
  setAttAt(now);
  setAttBy(volLbl);
}


//   async function markPresent() {
//     if (!roll || !slot) {
//       alert("Invalid QR: roll or slot is missing.");
//       return;
//     }
//     // recheck just before writing (avoid double taps / race)
//     const existing = await get(ref(db, `attendance/${roll}/${slot}`));
//     if (existing.exists()) {
//       const v = existing.val() || {};
//       setAlready(true);
//       setAttAt(v.at || v.t || null);
//       setAttBy(v.byVol || v.v || "");
//       alert("Already checked-in for this slot.");
//       return;
//     }

//     const now = Date.now();
//     // volunteer identity string
//     const volId   = volInfo?.id || auth.currentUser?.uid || "";
//     const volMail = volInfo?.email || auth.currentUser?.email || "";
//     const volLbl  = volId || volMail || "unknown";

//     // Write both trees + audit
//     const updates = {};
//     updates[`attendance/${roll}/${slot}`] = { at: now, byVol: volLbl };
//     updates[`attendanceBySlot/${slot}/${roll}`] = { at: now, byVol: volLbl };
//     updates[`audit/checkins/${now}_${roll}`] = {
//       roll, slot, at: now,
//       by: volLbl,
//       byName: volInfo?.name || "",
//       byEmail: volMail
//     };
//     await update(ref(db), updates);

//     setAlready(true);
//     setAttAt(now);
//     setAttBy(volLbl);
//     alert(`Marked present for ${roll} at ${new Date(now).toLocaleString()}`);
//   }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold">Volunteer — Check-in</h1>

      <div className="bg-white rounded-2xl shadow p-6 space-y-4">
        {loading && <div className="text-sm text-gray-500">Loading participant…</div>}

        {!loading && (!roll || !slot) && (
          <div className="text-sm text-red-600">
            Invalid QR or missing parameters. (roll: {roll || "-"}, slot: {slot || "-"})
          </div>
        )}

        {!loading && card && (
          <>
            <div className="p-4 rounded-xl border bg-gray-50">
              <div className="font-semibold">{card.name}</div>
              <div className="text-gray-600 text-sm">
                {card.roll}
                {card.branch ? ` • ${card.branch}` : ""}
                {card.section ? ` • ${card.section}` : ""}
                {card.teamId ? ` • Team: ${card.teamId}` : ""}
                {card.email ? ` • ${card.email}` : ""}
              </div>
            </div>

            {already ? (
              <div className="p-4 rounded-xl border bg-green-50 text-green-700">
                Already checked-in for this slot.
                {attAt ? ` Time: ${new Date(attAt).toLocaleString()}` : ""}
                {attBy ? ` • By: ${attBy}` : ""}
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={markPresent}
                  className="px-4 py-3 rounded-xl bg-black text-white hover:opacity-90"
                >
                  Mark Present (this slot)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
